// Theme switcher — dark (default) vs light (parchment + cherry).
//
// Implementation: writes `data-theme="light"` on <html> and persists
// the user's choice in localStorage. The actual color overrides live
// in src/client/index.css under the `[data-theme="light"]` block.
//
// We apply the persisted theme synchronously at module load (before
// React renders) so there's no flash-of-dark on a light-mode reload.

import { useEffect, useState } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "njrc.theme";

function readStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

function applyTheme(t: Theme): void {
  if (typeof document === "undefined") return;
  if (t === "light") {
    document.documentElement.setAttribute("data-theme", "light");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

// Apply IMMEDIATELY at first import — runs before React mounts so the
// page paints in the correct theme. Skipped during SSR (no document).
if (typeof document !== "undefined") {
  applyTheme(readStoredTheme());
}

/**
 * Read + control the active theme. Mounts a small cross-tab sync so
 * toggling in one tab updates the other. Persisted in localStorage.
 */
export function useTheme(): {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
} {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Cross-tab sync — if the user flips the theme in another tab, mirror it here.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setThemeState(e.newValue === "light" ? "light" : "dark");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* ignore quota */ }
  };

  const toggle = () => setTheme(theme === "dark" ? "light" : "dark");

  return { theme, setTheme, toggle };
}
